import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { searchSongs } from '../utils/songUtils';
import SongListItem from './SongListItem';
import { FaSearch } from 'react-icons/fa';

const SectionContainer = styled.div`
  margin: 50px 0;
  width: 100%;
  box-sizing: border-box;
  opacity: ${props => props.$visible ? 1 : 0};
  transform: translateY(${props => props.$visible ? '0' : '-20px'});
  transition: opacity 0.3s ease-out, transform 0.3s ease-out;
  position: ${props => props.$visible ? 'relative' : 'absolute'};
  top: 0;
  left: 0;
  right: 0;

  @media (min-width: 769px) and (max-width: 1280px) {
    padding: 0 15px;
  }

  @media (max-width: 768px) {
    padding: 0 15px;
  }
`;

const SectionTitle = styled.h2`
  font-family: 'DM Serif Text', serif;
  color: #2E2E2E;
  margin-bottom: 30px;
  text-align: left;
  display: flex;
  align-items: center;
  gap: 10px;

  svg {
    font-size: 1.2em;
  }

  @media (max-width: 768px) {
    text-align: center;
    justify-content: center;
  }
`;

const SongsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  width: 100%;
`;

const LoadingContainer = styled.div`
  padding: 20px;
  text-align: center;
  color: #2E2E2E;
  font-size: 16px;
`;

const ErrorContainer = styled.div`
  padding: 20px;
  text-align: center;
  color: #ff4444;
  font-size: 16px;
`;

function SearchResultsSection({ searchQuery, visible = true }) {
  const [songs, setSongs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchSearchResults = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const results = await searchSongs(searchQuery);
        
        // Sort songs (featured first, then by date)
        const sortedSongs = results
          .sort((a, b) => {
            // Featured songs come first
            if (a.song_featured && !b.song_featured) return -1;
            if (!a.song_featured && b.song_featured) return 1;
            // Then sort by date
            return new Date(b.date_added) - new Date(a.date_added);
          })
          .slice(0, 25); // Get only top 25 songs
        
        setSongs(sortedSongs);
      } catch (error) {
        console.error('Error fetching search results:', error);
        setError(error.message);
      } finally {
        setLoading(false);
      }
    };

    if (searchQuery) {
      fetchSearchResults();
    }
  }, [searchQuery]);

  if (loading) {
    return (
      <SectionContainer $visible={visible}>
        <SectionTitle>
          <FaSearch />
          Results for "{searchQuery}"
        </SectionTitle>
        <LoadingContainer>
          Loading tracks...
        </LoadingContainer>
      </SectionContainer>
    );
  }

  if (error) {
    return (
      <SectionContainer $visible={visible}>
        <SectionTitle>
          <FaSearch />
          Results for "{searchQuery}"
        </SectionTitle>
        <ErrorContainer>
          Error loading tracks. Please try again later.
        </ErrorContainer>
      </SectionContainer>
    );
  }

  if (songs.length === 0) {
    return (
      <SectionContainer $visible={visible}>
        <SectionTitle>
          <FaSearch />
          Results for "{searchQuery}"
        </SectionTitle>
        <LoadingContainer>
          No tracks found matching your search.
        </LoadingContainer>
      </SectionContainer>
    );
  }

  return (
    <SectionContainer $visible={visible}>
      <SectionTitle>
        <FaSearch />
        Results for "{searchQuery}"
      </SectionTitle>
      <SongsList>
        {songs.map((song) => (
          <SongListItem 
            key={song.song_id} 
            song={song}
            shouldTrackPlay={false}
          />
        ))}
      </SongsList>
    </SectionContainer>
  );
}

export default SearchResultsSection; 